import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface ElementMenu {
    icone: React.ReactNode;
    titre: string;
    description?: string;
    onClick: () => void;
    // Trait de séparation au-dessus de cet élément.
    separe?: boolean;
}

interface Props {
    // Contenu du bouton qui ouvre le menu : une icône seule (alors donner `etiquetteAccessible`) ou icône + texte.
    bouton: React.ReactNode;
    etiquetteAccessible?: string;
    elements: ElementMenu[];
    aligneADroite?: boolean;
    avecFleche?: boolean;
}

// Menu qui range plusieurs actions derrière un seul bouton (planning de prestation : 13 boutons à plat s'y perdaient).
export const MenuDeroulant: React.FC<Props> = ({ bouton, etiquetteAccessible, elements, aligneADroite = true, avecFleche = true }) => {
    const [ouvert, setOuvert] = useState(false);
    const racine = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ouvert) return;
        const dehors = (ev: MouseEvent) => { if (racine.current && !racine.current.contains(ev.target as Node)) setOuvert(false); };
        const echap = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOuvert(false); };
        document.addEventListener('mousedown', dehors);
        document.addEventListener('keydown', echap);
        return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
    }, [ouvert]);

    return (
        <div ref={racine} className="relative">
            <button type="button" onClick={() => setOuvert((o) => !o)} aria-haspopup="menu" aria-expanded={ouvert} aria-label={etiquetteAccessible}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content hover:bg-surface-soft text-xs font-bold transition-colors">
                {bouton}
                {avecFleche && <ChevronDown size={13} className={`text-content-muted transition-transform ${ouvert ? 'rotate-180' : ''}`} aria-hidden />}
            </button>
            {ouvert && (
                <div role="menu" className={`absolute mt-2 w-80 max-w-[90vw] bg-surface border border-border rounded-2xl shadow-2xl z-40 p-1.5 ${aligneADroite ? 'right-0' : 'left-0'}`}>
                    {elements.map((el) => (
                        <React.Fragment key={el.titre}>
                            {el.separe && <div className="my-1 border-t border-border" />}
                            <button type="button" role="menuitem" onClick={() => { setOuvert(false); el.onClick(); }}
                                className="w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-xl hover:bg-surface-soft transition-colors">
                                <span className="mt-0.5 text-content-muted shrink-0" aria-hidden>{el.icone}</span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-bold text-content">{el.titre}</span>
                                    {el.description && <span className="block text-[11px] text-content-muted leading-snug mt-0.5">{el.description}</span>}
                                </span>
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};
